"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { BlogCategory } from "@/lib/blogs/get-posts";

type Post = {
  slug: string;
  title: string;
  description: string;
  date: string;
  author: string;
  readTime: string;
  image: string;
  imageAlt: string;
  category: BlogCategory;
};

type Tab = "All" | BlogCategory;
const TABS: Tab[] = ["All", "Ideation", "Building", "Growth"];
const PAGE_SIZE = 8;

export default function BlogMoreArticles({
  posts,
  heading = "All articles",
}: {
  posts: Post[];
  heading?: string;
}) {
  const [activeTab, setActiveTab] = useState<Tab>("All");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      activeTab === "All"
        ? posts
        : posts.filter((p) => p.category === activeTab),
    [activeTab, posts],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  return (
    <section className="w-full max-w-5xl mx-auto px-6 mt-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">{heading}</h2>
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeTab === tab
                  ? "bg-primary text-white border-primary"
                  : "bg-ink-soft text-zinc-400 border-line hover:bg-panel-light"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-line">
        {visible.map((post) => (
          <Link
            key={post.slug}
            href={`/blog/${post.slug}`}
            className="group block py-6 border-b border-line"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                {post.category}
              </span>
              <span className="text-xs text-zinc-500">·</span>
              <span className="text-xs text-zinc-400">
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="text-xs text-zinc-500">·</span>
              <span className="text-xs text-zinc-400">{post.readTime}</span>
            </div>
            <h3 className="text-xl font-semibold text-white group-hover:text-primary transition-colors leading-snug">
              {post.title}
            </h3>
            <p className="text-base text-zinc-400 leading-relaxed mt-2">
              {post.description}
            </p>
          </Link>
        ))}
      </div>

      {totalPages > 1 && (
        <nav
          className="flex items-center justify-center gap-1 mt-10"
          aria-label="Pagination"
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-400 hover:bg-panel-light disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              aria-current={p === currentPage ? "page" : undefined}
              className={`min-w-9 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                p === currentPage
                  ? "bg-primary text-white"
                  : "text-zinc-400 hover:bg-panel-light"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-400 hover:bg-panel-light disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
